import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Loader2, HardHat, AlertTriangle, CheckCircle, Package, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_PPE = {
  summary: {
    total_items: 156,
    in_stock: 142,
    low_stock: 8,
    out_of_stock: 6,
  },
  items: [
    { id: '1', name: 'Safety Helmets', quantity: 45, min_quantity: 20, status: 'ok' },
    { id: '2', name: 'High-Vis Vests', quantity: 12, min_quantity: 30, status: 'low' },
    { id: '3', name: 'Safety Glasses', quantity: 0, min_quantity: 50, status: 'out' },
    { id: '4', name: 'Work Gloves', quantity: 8, min_quantity: 25, status: 'low' },
    { id: '5', name: 'Steel-Toe Boots', quantity: 32, min_quantity: 15, status: 'ok' },
  ]
};

const getStatusConfig = (status) => {
  const configs = {
    ok: { label: 'In Stock', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    low: { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
    out: { label: 'Out of Stock', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  };
  return configs[status] || configs.ok;
};

export function PPEStatusTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_PPE);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadPPEData = async () => {
      try {
        setLoading(true);

        // Load PPE inventory items
        const { data: items, error } = await supabase
          .from('ppe_inventory')
          .select('id, name, quantity, min_quantity')
          .eq('company_id', company.id)
          .order('quantity', { ascending: true })
          .limit(10);

        if (error) throw error;

        // Calculate statuses
        const processedItems = (items || []).map(item => ({
          ...item,
          status: item.quantity === 0 ? 'out' :
                  item.quantity <= item.min_quantity ? 'low' : 'ok'
        }));

        const summary = {
          total_items: processedItems.length,
          in_stock: processedItems.filter(i => i.status === 'ok').length,
          low_stock: processedItems.filter(i => i.status === 'low').length,
          out_of_stock: processedItems.filter(i => i.status === 'out').length,
        };

        setData({
          summary,
          items: processedItems.filter(i => i.status !== 'ok').slice(0, 5) // Show problematic items
        });
      } catch (error) {
        console.error('Failed to load PPE data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPPEData();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <HardHat className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No PPE data</p>
        <p className="text-xs text-gray-500 mt-1">Set up PPE tracking</p>
      </div>
    );
  }

  const hasIssues = data.summary.low_stock > 0 || data.summary.out_of_stock > 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Summary header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <div className={cn(
            "p-1.5 rounded-lg",
            hasIssues ? "bg-amber-100" : "bg-green-100"
          )}>
            <HardHat className={cn(
              "h-4 w-4",
              hasIssues ? "text-amber-600" : "text-green-600"
            )} />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-900">PPE Inventory</p>
            <p className="text-[10px] text-gray-500">{data.summary.total_items} items tracked</p>
          </div>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex gap-1.5 mb-2 shrink-0">
        <Badge className="bg-green-100 text-green-800 text-[10px]">
          {data.summary.in_stock} OK
        </Badge>
        {data.summary.low_stock > 0 && (
          <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">
            {data.summary.low_stock} Low
          </Badge>
        )}
        {data.summary.out_of_stock > 0 && (
          <Badge className="bg-red-100 text-red-800 text-[10px]">
            {data.summary.out_of_stock} Out
          </Badge>
        )}
      </div>

      {/* Items needing attention */}
      {data.items.length > 0 ? (
        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-2 pr-2">
            {data.items.map((item) => {
              const statusConfig = getStatusConfig(item.status);
              const stockPercent = item.min_quantity > 0
                ? Math.min(100, Math.round((item.quantity / item.min_quantity) * 100))
                : 100;

              return (
                <div
                  key={item.id}
                  className={cn(
                    "p-2 rounded-lg border transition-all",
                    item.status === 'out' ? "bg-red-50 border-red-200" :
                    item.status === 'low' ? "bg-yellow-50 border-yellow-200" :
                    "bg-white"
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-900 truncate flex-1">
                      {item.name}
                    </p>
                    <Badge className={cn("text-[10px] ml-2", statusConfig.color)}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={stockPercent}
                      className={cn(
                        "h-1.5 flex-1",
                        item.status === 'out' ? "[&>div]:bg-red-500" :
                        item.status === 'low' ? "[&>div]:bg-yellow-500" :
                        "[&>div]:bg-green-500"
                      )}
                    />
                    <span className="text-[10px] text-gray-600 shrink-0">
                      {item.quantity}/{item.min_quantity}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">All stocked!</p>
            <p className="text-xs text-gray-500">No items need attention</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <button
        onClick={() => navigate('/ppe')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium transition-colors shrink-0"
      >
        <Package className="h-3.5 w-3.5" />
        <span>Manage Inventory</span>
      </button>
    </div>
  );
}
